import { useState, useCallback, useMemo } from 'react';
import { MenuProps as MuiMenuProps } from '@mui/material/Menu';

interface AutoPositionOptions {
  desiredMaxHeight?: number;
  viewMargin?: number;
}

export function useAutoPositionedMenu(options: AutoPositionOptions = {}) {
  const { desiredMaxHeight = 240, viewMargin = 16 } = options;

  const [menuState, setMenuState] = useState<{
    openUpward: boolean;
    maxHeight: number;
  }>({
    openUpward: false,
    maxHeight: desiredMaxHeight,
  });

  const handleOpen = useCallback(
    (e: React.SyntheticEvent) => {
      const target = e.currentTarget as HTMLElement;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const spaceBelow = windowHeight - rect.bottom - viewMargin;
      const spaceAbove = rect.top - viewMargin;

      const openUpward = spaceBelow < desiredMaxHeight && spaceAbove > spaceBelow;
      const availableSpace = openUpward ? spaceAbove : spaceBelow;
      const calculatedMaxHeight = Math.min(desiredMaxHeight, Math.max(120, availableSpace));

      setMenuState({
        openUpward,
        maxHeight: calculatedMaxHeight,
      });
    },
    [desiredMaxHeight, viewMargin]
  );

  const menuProps: Partial<MuiMenuProps> = useMemo(
    () => ({
      anchorOrigin: menuState.openUpward
        ? { vertical: 'top', horizontal: 'left' }
        : { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: menuState.openUpward
        ? { vertical: 'bottom', horizontal: 'left' }
        : { vertical: 'top', horizontal: 'left' },
      PaperProps: {
        sx: {
          maxHeight: menuState.maxHeight,
          borderRadius: 1.5,
          mt: menuState.openUpward ? 0 : 0.5,
          mb: menuState.openUpward ? 0.5 : 0,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
        },
      },
    }),
    [menuState.openUpward, menuState.maxHeight]
  );

  return {
    onOpen: handleOpen,
    MenuProps: menuProps,
  };
}
